<script lang="ts" setup>
import * as Sentry from '@sentry/vue';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { UPDATE_CLOSED_COUNT_LS_KEY } from '@/types/components/rich-tooltips/UpdateAvailable.consts';
import { version } from '../../../package.json';
import { useI18n } from 'vue-i18n';
import RichTooltip from '../RichTooltip.vue';
import useUserService from '@/composables/apiServices/useUserService';
import { useRoute } from 'vuetify/lib/composables/router.mjs';
import { ROUTE_NAMES } from '@/router/conf';
import { useServiceWorkerUpdates } from '@/composables/useServiceWorkerUpdates';
import { recoverFromChunkLoadError } from '@/helpers/chunkRecovery';
/**
 * This component is responsible for displaying the "update available" tooltip. It is also responsible for handling the service worker updates.
 */
const { t } = useI18n();
const route = useRoute();
const { details } = useUserService();
const { data: userDetails } = details();

// This is default true to hide the tooltip for now. It SHOULD BE set to false when we have implemented the logic for showing what has changed in this update to the user.
const hideUpdateTooltip = ref(true);
const updateClosedCount = ref(Number(localStorage.getItem(UPDATE_CLOSED_COUNT_LS_KEY) || '0'));

const intervalMS = 1000 * Number(import.meta.env.VITE_SERVICE_WORKER_UPDATE_INTERVAL ?? 15 * 60); // Default to 15 minutes
const serviceWorkerUpdateLoading = ref(false);

let lastETag: string | null = null;
let lastLastModified: string | null = null;

const checkForUpdates = async (swUrl: string) => {
  const url = new URL(swUrl, window.location.href);
  url.searchParams.set('version', version);

  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache'
  };
  if (userDetails.value) {
    headers['X-User-WD-id'] = userDetails.value.wdId.toString();
    headers['X-User-id'] = userDetails.value.id.toString();
  }
  const resp = await fetch(url, {
    method: 'HEAD',
    cache: 'no-store',
    headers
  }).catch((err) => {
    console.error('Error fetching service worker for update check:', err);
    if (!(err instanceof TypeError)) {
      Sentry.captureException(err, { tags: { feature: 'service-worker-pre-check' } });
    }
    return null;
  });

  if (resp?.ok) {
    const currentETag = resp.headers.get('etag');
    const currentLastModified = resp.headers.get('last-modified');

    if (lastETag && lastETag !== currentETag) {
      console.debug('File has changed!');
      lastETag = currentETag;
      return true;
    }
    lastETag = currentETag;

    if (lastLastModified && lastLastModified !== currentLastModified) {
      console.debug('File has changed!');
      lastLastModified = currentLastModified;
      return true;
    }
    lastLastModified = currentLastModified;
  }

  return false;
};
// Use offlineReady when we have the tooltip for it
const { needRefresh, updateServiceWorker } = useServiceWorkerUpdates({
  /**
   * @see https://vite-pwa-org.netlify.app/guide/periodic-sw-updates.html#handling-edge-cases
   */
  onRegisteredSW(swUrl, swr) {
    console.debug('Service worker registered at:', swUrl);

    if (Number.isNaN(intervalMS) || !swr) {
      console.warn('Service worker update interval is not a number, skipping interval update!');
      return;
    }

    setInterval(async () => {
      console.debug('Checking for service worker updates...');
      if (!(!swr.installing && navigator)) return;
      console.debug('Service worker is ready, checking for updates...');
      if ('connection' in navigator && !navigator.onLine) return;
      console.debug('Network is online, checking for updates...');
      if (await checkForUpdates(swUrl)) {
        console.debug('File has changed, updating...');
        needRefresh.value = true;
        await swr.update().catch((err) => {
          console.error('Error updating service worker:', err);
          Sentry.captureException(err, { tags: { feature: 'service-worker' } });
        });
      }
    }, intervalMS);
  }
});

const handleUpdateClosed = () => {
  // This function is called when the user closes the update available tooltip.
  // It iterates a counter in localstorage to keep track of how many times the user has closed the tooltip.
  updateClosedCount.value = updateClosedCount.value + 1;
  localStorage.setItem(UPDATE_CLOSED_COUNT_LS_KEY, updateClosedCount.value.toString());
  console.debug(`Update closed count: ${updateClosedCount.value}`);

  hideUpdateTooltip.value = true;
};

const handleUpdateServiceWorker = async () => {
  console.debug('Handling service worker update!');
  serviceWorkerUpdateLoading.value = true;
  await nextTick();
  try {
    // Reset the update closed count in localStorage
    localStorage.setItem(UPDATE_CLOSED_COUNT_LS_KEY, '0');
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.waiting) {
      console.debug('No service worker is waiting! Reloading page to activate new service worker.');
      window.location.reload();
    } else {
      console.debug('Service worker is waiting! Calling update.');
      await updateServiceWorker();
    }
    console.debug('Handled service worker update!');
  } catch (e) {
    console.debug('Service worker update failed!');
    console.error(e);
    window.location.reload();
  }
};

const preloadErrorAbortController = new AbortController();
window.addEventListener(
  'vite:preloadError',
  (event) => {
    recoverFromChunkLoadError(event, { reload: false });
  },
  { signal: preloadErrorAbortController.signal }
);
onMounted(async () => {
  if (navigator.serviceWorker) {
    const swUrl = navigator.serviceWorker.controller?.scriptURL;
    if (swUrl) {
      await checkForUpdates(swUrl).catch((err) => {
        console.error('Error checking for service worker updates:', err);
      });
    }

    const registration = await navigator.serviceWorker.getRegistration();

    if (registration?.waiting && registration.waiting.state === 'installed') {
      // I don't rly like this timeout, but without it the service worker doesn't activate properly.
      setTimeout(() => {
        handleUpdateServiceWorker();
      }, 50);
    }
  }
});

onBeforeUnmount(() => {
  preloadErrorAbortController.abort();
});

watch(
  () => route.value?.fullPath,
  () => {
    if (
      route.value?.name === ROUTE_NAMES.COMPOSE ||
      route.value?.name === ROUTE_NAMES.COMPOSE_TAB
    ) {
      return;
    }

    if (!needRefresh.value) {
      return;
    }

    handleUpdateServiceWorker();
  }
);

watch(
  needRefresh,
  (newVal) => {
    if (newVal) {
      console.debug(
        'New service worker update is available! Starting timer for inactivity reload.'
      );
      setTimeout(
        () => {
          if (needRefresh.value) {
            console.debug('Reloading page due to inactivity and available service worker update.');
            handleUpdateServiceWorker();
          }
        },
        (Number(import.meta.env.VITE_SERVICE_WORKER_UPDATE_INACTIVITY_TIMEOUT_SECONDS) || 5 * 60) *
          1000
      ); // env or 5 minute default seconds
    }
  },
  { immediate: true }
);
</script>

<template>
  <RichTooltip
    v-if="needRefresh && !hideUpdateTooltip"
    :is-loading="serviceWorkerUpdateLoading"
    :content="t('updateAvailable.content')"
    :next-label="t('updateAvailable.actions.reload')"
    :hide-remind-later="updateClosedCount >= 2"
    @next-tooltip="handleUpdateServiceWorker"
    @skip="handleUpdateClosed"
    @remind-later="handleUpdateClosed"
    hide-close
  />
</template>
